import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { MaskLines, Overline } from "./Motion";
import { IMAGES } from "../../data/content";

const Chip = ({ className, children, delay }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    className={`absolute z-20 rounded-[4px] border border-black/10 bg-white/85 px-4 py-3 shadow-[0_20px_50px_-20px_rgba(13,15,20,0.35)] backdrop-blur-md ${className}`}
  >
    {children}
  </motion.div>
);

const AxisFrame = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={reset} className="relative mx-auto aspect-[4/5] w-full max-w-[520px]" style={{ perspective: 1200 }} data-testid="hero-3d-frame">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative h-full w-full">
        <svg className="spin-slow absolute -inset-[14%] h-[128%] w-[128%] text-[#0D0F14]/25" viewBox="0 0 200 200" style={{ transform: "translateZ(-60px)" }} aria-hidden="true">
          {[96, 76, 56].map((r) => <circle key={r} cx="100" cy="100" r={r} fill="none" stroke="currentColor" strokeWidth="0.35" strokeDasharray="1 3" />)}
          <line x1="0" y1="100" x2="200" y2="100" stroke="currentColor" strokeWidth="0.35" />
          <line x1="100" y1="0" x2="100" y2="200" stroke="currentColor" strokeWidth="0.35" />
        </svg>
        <motion.div
          initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: 1.5, ease: [0.76, 0, 0.24, 1], delay: 0.35 }}
          className="absolute inset-0 overflow-hidden rounded-[4px] bg-[var(--ink)]"
          style={{ transform: "translateZ(0px)" }}
        >
          <img src={IMAGES.hero} alt="Wireaxis research strategy session" className="h-full w-full object-cover opacity-90 grayscale-[30%]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_25%,transparent_0%,rgba(13,15,20,0.55)_70%)]" />
          <div className="absolute inset-0 mix-blend-overlay bg-[#2337FF]/40" />
        </motion.div>
        <div style={{ transform: "translateZ(70px)" }} className="absolute inset-0">
          <Chip delay={1.3} className="left-3 top-[10%] lg:-left-10">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">Signal</p>
            <p className="mt-1 text-sm font-semibold">Customer sentiment ↑</p>
          </Chip>
          <Chip delay={1.5} className="right-3 bottom-[10%] lg:right-[-1.5rem]">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">Axis</p>
            <div className="mt-2 flex h-10 items-end gap-1.5">
              {[40, 64, 52, 80, 96].map((h, i) => (
                <motion.span key={i} style={{ height: `${h}%`, originY: 1 }} initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ delay: 1.7 + i * 0.08, duration: 0.6 }} className={`w-2.5 rounded-sm ${i === 4 ? "bg-[var(--cobalt)]" : "bg-[#0D0F14]/80"}`} />
              ))}
            </div>
          </Chip>
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative overflow-hidden pt-28 pb-16 sm:pt-36 lg:min-h-screen">
      <div className="grid-lines pointer-events-none absolute inset-0 [mask-image:radial-gradient(ellipse_at_top_left,black_20%,transparent_70%)]" />
      <div className="relative mx-auto grid max-w-[1400px] gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:items-center">
        <motion.div style={{ y }} className="lg:col-span-7">
          <Overline>Market research agency · Est. by young professionals</Overline>
          <h1 className="mt-7 text-[13vw] font-semibold leading-[0.9] tracking-[-0.05em] sm:text-7xl lg:text-[6.6rem]" data-testid="hero-headline">
            <MaskLines lines={["We read", <>markets <span className="font-serif-i text-[var(--cobalt)]">before</span></>, "they move."]} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-[var(--muted)] sm:text-lg"
          >
            Wireaxis delivers high-value market research operations and expert consultancy, with best-in-class quality at competitive pricing, so you can win customers, sharpen your edge and refine strategy.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.9 }} className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/contact" data-testid="hero-primary-cta" className="group inline-flex items-center gap-3 rounded-full bg-[var(--ink)] py-2 pl-6 pr-2 text-sm font-semibold text-[var(--paper)] transition-colors hover:bg-[var(--cobalt)]">
              Request a proposal
              <span className="grid h-9 w-9 place-items-center rounded-full bg-[var(--paper)] text-[var(--ink)] transition-transform group-hover:rotate-45"><ArrowUpRight className="h-4 w-4" /></span>
            </Link>
            <Link to="/services" data-testid="hero-secondary-cta" className="group inline-flex items-center gap-2 px-2 py-3 text-sm font-semibold">
              <span className="border-b border-[var(--ink)] pb-0.5">Explore services</span>
              <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-1" />
            </Link>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5"><AxisFrame /></div>
      </div>
    </section>
  );
};
